import * as React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Spinner } from '@factorialco/deprecated-design-system'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import useGetBookkeeperCompanies from 'modules/bookkeepers/hooks/useGetBookkeeperCompanies'
import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import { TaskTypesProvider } from 'modules/bookkeepers/lib/TaskTypesContext'
import { trackViewIncidenceDetail } from 'modules/bookkeepers/lib/amplitudeData'
import { useBookkeeperView } from 'modules/bookkeepers/lib/bookkeeperViewContext'
import { FilesProvider } from 'modules/bookkeepers/store/collections/files'
import { IncidenceMessagesProvider } from 'modules/bookkeepers/store/collections/incidenceMessages'
import incidenceNotes, {
  IncidenceNotesProvider,
} from 'modules/bookkeepers/store/collections/incidenceNotes'
import taskTypes from 'modules/bookkeepers/store/collections/taskTypes'
import useFetchIncidenceMessages from 'modules/payroll_bundle/hooks/useFetchIncidenceMessages'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import legalEntitiesSettings from 'modules/payroll_exporter/store/collections/legalEntitiesSettings'

import HeadInformation from './HeadInformation'

interface Props {
  incidenceDetail: React.ReactNode
  toggleEditModal?: (value: boolean) => () => void
}

const IncidenceInformation = ({ incidenceDetail, toggleEditModal }: Props) => {
  const { id } = useParams()
  const isBookkeeperView = useBookkeeperView()
  const { hasInhousePayroll } = useInhousePayroll()
  const incidence = useCurrentIncidence()
  const { loading: loadingBookkeeperCompanies, bookkeeperCompanies } = useGetBookkeeperCompanies()
  const incidenceCompanyId = incidence?.legalEntity?.companyId

  const incidenceName = incidence?.name
  const incidenceId = incidence?.id

  const { loading: loadingTasksAndNotes } = useAsyncData(async () => {
    if (!(isBookkeeperView || hasInhousePayroll) || incidenceId === null) {
      return null
    }
    return Promise.all([
      incidenceNotes.fetch({
        data: {
          bookkeeper_view: isBookkeeperView,
          incidence_id: incidenceId,
        },
      }),
      legalEntitiesSettings.fetch({
        data: {
          bookkeeper_view: isBookkeeperView,
        },
      }),
    ])
  }, [isBookkeeperView, incidenceId, incidenceCompanyId, incidenceName])

  const { loading: loadingTasks } = useAsyncData(async () => {
    if (loadingBookkeeperCompanies) {
      return null
    }
    const bookkeeperCompanyId = bookkeeperCompanies?.[0]?.companyId
    const referenceCompanyIds = bookkeeperCompanyId ? [bookkeeperCompanyId] : [incidenceCompanyId]
    return Promise.all([
      taskTypes.fetch({
        data: {
          bookkeeper_view: isBookkeeperView,
          company_ids: referenceCompanyIds,
          incidence_name: incidenceName,
          hidden: false,
        },
      }),
    ])
  }, [isBookkeeperView, loadingBookkeeperCompanies])

  const {
    loading: loadingMessages,
    incidenceMessagesCollection,
    filesCollection,
  } = useFetchIncidenceMessages({
    incidenceId,
    isBookkeeperView,
  })

  useUnsafeEffect(() => {
    if (id && incidence.id === id) {
      trackViewIncidenceDetail(incidence, isBookkeeperView, hasInhousePayroll)
    }
  }, [incidence, isBookkeeperView, id, hasInhousePayroll])

  const loading = loadingTasksAndNotes || loadingMessages || loadingTasks

  return (
    <ErrorBoundary>
      <Box paddingTop="s16">
        {incidence && !loading ? (
          <TaskTypesProvider collection={taskTypes}>
            <IncidenceNotesProvider collection={incidenceNotes}>
              <IncidenceMessagesProvider collection={incidenceMessagesCollection}>
                <FilesProvider collection={filesCollection}>
                  <HeadInformation
                    incidence={incidence}
                    isBookkeeperView={isBookkeeperView}
                    toggleEditModal={toggleEditModal}
                  />
                  {incidenceDetail}
                </FilesProvider>
              </IncidenceMessagesProvider>
            </IncidenceNotesProvider>
          </TaskTypesProvider>
        ) : (
          <Box
            width="full"
            height="full"
            alignItems="center"
            justifyContent="center"
            paddingTop="s48"
          >
            <Spinner />
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  )
}

export default IncidenceInformation
